// YERİ: src/app/domains/site/sections/section-host.ts
// Section registry — SectionType → görsel bileşen eşlemesi (@switch).
// Tek bir SiteSection alır, config'i parse eder, doğru bileşeni çizer.
// Yeni section tipi eklemek = buraya bir @case + bileşen importu.

import { Component, computed, input } from '@angular/core';
import { SiteSection } from '../core/tenant-web.service';
import { parseConfig } from './section.model';
import { HeroSection } from './hero.section';
import { PracticeAreasSection } from './practice-areas.section';
import { AboutSection } from './about.section';
import { StatsSection } from './stats.section';
import { TeamSection } from './team.section';
import { TestimonialsSection } from './testimonials.section';
import { FaqSection } from './faq.section';
import { CtaSection } from './cta.section';
import { ContactFormSection } from './contact-form.section';
import { FooterSection } from './footer.section';
import { MemberLoginSection } from './member-login.section';
import { EduProgramsSection } from './edu-programs.section';
import { AgencyHeroSection, AgencyServicesSection, AgencyCounterSection, AgencyCtaSection } from './agency.sections';
import { GallerySection } from './gallery.section';
import { MenuBarSection } from './menu-bar.section';
import { ServicesGridSection, PricingTableSection, ProductListSection } from './commerce.sections';
import { MapSection } from './map.section';
import { BlogPreviewSection } from './blog-preview.section';
import { FixtureListSection, StandingsTableSection, FormationPitchSection } from './sportsclub.sections';
import { PortfolioGridSection, ResumeTimelineSection } from './personal.sections';
import { SermonListSection, CommunityEventsSection, DonationCampaignsSection } from './community.sections';
import { ConsultingPackagesSection, EventPackagesSection, VenueGridSection, LoyaltyTiersSection } from './business.sections';
import { LiveMenuSection, RoomTypeGridSection, HotelAmenitiesSection } from './hospitality.sections';
import { PropertyListingsSection, VehicleShowcaseSection, StorageUnitsSection, BoardingUnitsSection, ProduceListSection } from './inventory.sections';
import { CourseCatalogSection, ShippingRatesSection, ProjectShowcaseSection, RateCardSection, SocialChannelsSection, ServicePricingSection, DaycareProgramsSection } from './offering.sections';
import { BookingWidgetSection } from './booking-widget.section';
import { PaymentBoxSection } from './payment-box.section';
import {
  MenuDisplaySection, TableReservationSection, RoomGridSection, ListingSearchSection,
  ClassScheduleSection, MembershipPlansSection, StaffGridSection, AppointmentWidgetSection,
} from './sector.sections';
import { AnchorNavSection } from './anchor-nav.section';
import { SliderSection } from './slider.section';
import { WsReveal } from './ws-reveal.directive';
import { TextBlock, ImageBlock, ButtonBlock } from './column-blocks';
import { TranslocoPipe } from '@jsverse/transloco';

const COL_SPAN: Record<number, string> = {
  1: 'md:col-span-1', 2: 'md:col-span-2', 3: 'md:col-span-3', 4: 'md:col-span-4',
  5: 'md:col-span-5', 6: 'md:col-span-6', 7: 'md:col-span-7', 8: 'md:col-span-8',
  9: 'md:col-span-9', 10: 'md:col-span-10', 11: 'md:col-span-11', 12: 'md:col-span-12',
};

@Component({
  selector: 'sec-host',
  standalone: true,
  imports: [FixtureListSection, StandingsTableSection, FormationPitchSection,
    PortfolioGridSection, ResumeTimelineSection,
    SermonListSection, CommunityEventsSection, DonationCampaignsSection,
    ConsultingPackagesSection, EventPackagesSection, VenueGridSection, LoyaltyTiersSection,
    LiveMenuSection, RoomTypeGridSection, HotelAmenitiesSection,
    PropertyListingsSection, VehicleShowcaseSection, StorageUnitsSection,
    BoardingUnitsSection, ProduceListSection,
    CourseCatalogSection, ShippingRatesSection, ProjectShowcaseSection, RateCardSection,
    SocialChannelsSection, ServicePricingSection, DaycareProgramsSection,
    MenuDisplaySection, TableReservationSection, RoomGridSection, ListingSearchSection,
    ClassScheduleSection, MembershipPlansSection, StaffGridSection, AppointmentWidgetSection,
    HeroSection, PracticeAreasSection, AboutSection, StatsSection, TeamSection,
    TestimonialsSection, FaqSection, CtaSection, ContactFormSection, FooterSection,
    MemberLoginSection, EduProgramsSection,
    AgencyHeroSection, AgencyServicesSection, AgencyCounterSection, AgencyCtaSection,
    GallerySection, MenuBarSection,
    ServicesGridSection, PricingTableSection, ProductListSection, MapSection, BlogPreviewSection, BookingWidgetSection,
    PaymentBoxSection,
    AnchorNavSection, SliderSection, TextBlock, ImageBlock, ButtonBlock, WsReveal, TranslocoPipe],
  template: `
    <!-- Giriş efekti TEK NOKTADAN: her bölüm tipine ayrı ayrı eklenseydi 37
         bileşen dokunulacak ve yeni bölümde unutulacaktı. Bölümün kendi
         config'indeki 'animation' alanı sürüyor; editör önizlemesinde kapalı. -->
    <!-- Yazı rengi/boyutu bölüm bazında:
         • boyut → zoom (temadaki fontScale ile aynı yöntem; Tailwind'in rem
           tabanlı text-* sınıflarını da ölçekleyen tek yol),
         • renk → --ws-text değişkeni + data-ws-text işareti; kural
           site-decor.css'te ve tema kuralından DAHA SEÇİCİ, böylece bölüm
           değeri temayı ezer. İşaret yalnız renk verilmişse konur — aksi halde
           renk vermeyen bölümlerin kendi renkleri bozulurdu. -->
    <div [wsReveal]="anim()" [wsRevealDisabled]="preview()"
         [attr.data-ws-text]="textColor() ? '' : null"
         [style.--ws-text]="textColor() || null"
         [attr.data-ws-btn]="buttonColor() ? '' : null"
         [style.--ws-btn-bg]="buttonColor() || null"
         [attr.data-ws-btn-fg]="buttonTextColor() ? '' : null"
         [style.--ws-btn-fg]="buttonTextColor() || null"
         [style.zoom]="fontScale()">
    @switch (section().sectionType) {
      @case ('hero')            { <sec-hero [config]="cfg()" /> }
      @case ('practice-areas')  { <sec-practice-areas [config]="cfg()" /> }
      @case ('about')           { <sec-about [config]="cfg()" /> }
      @case ('stats')           { <sec-stats [config]="cfg()" /> }
      @case ('team')            { <sec-team [config]="cfg()" /> }
      @case ('testimonials')    { <sec-testimonials [config]="cfg()" /> }
      @case ('faq')             { <sec-faq [config]="cfg()" /> }
      @case ('cta')             { <sec-cta [config]="cfg()" /> }
      @case ('contact-form')    { <sec-contact-form [config]="cfg()" [clientId]="clientId()" /> }
      @case ('member-login')    { <sec-member-login [config]="cfg()" [clientId]="clientId()" [companyUid]="companyUid()" /> }
      @case ('edu-programs')    { <sec-edu-programs [config]="cfg()" /> }
      @case ('agency-hero')     { <sec-agency-hero [config]="cfg()" /> }
      @case ('agency-services') { <sec-agency-services [config]="cfg()" /> }
      @case ('agency-counter')  { <sec-agency-counter [config]="cfg()" /> }
      @case ('agency-cta')      { <sec-agency-cta [config]="cfg()" /> }
      @case ('gallery')         { <sec-gallery [config]="cfg()" /> }
      @case ('menu-bar')        { <sec-menu-bar [config]="cfg()" [clientId]="clientId()" [preview]="preview()" /> }
      @case ('services-grid')   { <sec-services-grid [config]="cfg()" /> }
      @case ('pricing-table')   { <sec-pricing-table [config]="cfg()" /> }
      @case ('product-list')    { <sec-product-list [config]="cfg()" [clientId]="clientId()" /> }
      @case ('map')             { <sec-map [config]="cfg()" /> }
      @case ('blog-preview')    { <sec-blog-preview [config]="cfg()" [clientId]="clientId()" /> }
      <!-- Spor kulübü: üçü de veriyi SUNUCUDAN çeker, bölüm ayarından değil. -->
      @case ('fixture-list')      { <sec-fixture-list [config]="cfg()" [clientId]="clientId()" /> }
      @case ('standings-table')   { <sec-standings-table [config]="cfg()" [clientId]="clientId()" /> }
      @case ('formation-pitch')   { <sec-formation-pitch [config]="cfg()" [clientId]="clientId()" /> }
      <!-- Sektörel: hepsi veriyi SUNUCUDAN çeker, bölüm ayarından değil. -->
      @case ('portfolio-grid')      { <sec-portfolio-grid [config]="cfg()" [clientId]="clientId()" /> }
      @case ('resume-timeline')     { <sec-resume-timeline [config]="cfg()" [clientId]="clientId()" /> }
      @case ('sermon-list')         { <sec-sermon-list [config]="cfg()" [clientId]="clientId()" /> }
      @case ('community-events')    { <sec-community-events [config]="cfg()" [clientId]="clientId()" /> }
      @case ('donation-campaigns')  { <sec-donation-campaigns [config]="cfg()" [clientId]="clientId()" /> }
      @case ('consulting-packages') { <sec-consulting-packages [config]="cfg()" [clientId]="clientId()" /> }
      @case ('event-packages')      { <sec-event-packages [config]="cfg()" [clientId]="clientId()" /> }
      @case ('venue-grid')          { <sec-venue-grid [config]="cfg()" [clientId]="clientId()" /> }
      @case ('loyalty-tiers')       { <sec-loyalty-tiers [config]="cfg()" [clientId]="clientId()" /> }
      @case ('live-menu')             { <sec-live-menu [config]="cfg()" [clientId]="clientId()" /> }
      @case ('roomtype-grid')         { <sec-roomtype-grid [config]="cfg()" [clientId]="clientId()" /> }
      @case ('hotel-amenities')       { <sec-hotel-amenities [config]="cfg()" [clientId]="clientId()" /> }
      @case ('property-listings')     { <sec-property-listings [config]="cfg()" [clientId]="clientId()" /> }
      @case ('vehicle-showcase')      { <sec-vehicle-showcase [config]="cfg()" [clientId]="clientId()" /> }
      @case ('storage-units')         { <sec-storage-units [config]="cfg()" [clientId]="clientId()" /> }
      @case ('boarding-units')        { <sec-boarding-units [config]="cfg()" [clientId]="clientId()" /> }
      @case ('produce-list')          { <sec-produce-list [config]="cfg()" [clientId]="clientId()" /> }
      @case ('course-catalog')        { <sec-course-catalog [config]="cfg()" [clientId]="clientId()" /> }
      @case ('shipping-rates')        { <sec-shipping-rates [config]="cfg()" [clientId]="clientId()" /> }
      @case ('project-showcase')      { <sec-project-showcase [config]="cfg()" [clientId]="clientId()" /> }
      @case ('rate-card')             { <sec-rate-card [config]="cfg()" [clientId]="clientId()" /> }
      @case ('social-channels')       { <sec-social-channels [config]="cfg()" [clientId]="clientId()" /> }
      @case ('service-pricing')       { <sec-service-pricing [config]="cfg()" [clientId]="clientId()" /> }
      @case ('daycare-programs')      { <sec-daycare-programs [config]="cfg()" [clientId]="clientId()" /> }
      @case ('booking-widget')  { <sec-booking-widget [config]="cfg()" [clientId]="clientId()" /> }
      <!-- sectionId ŞART: tutar sunucuda bu kimlikle bulunuyor. -->
      @case ('payment-box')     { <sec-payment-box [config]="cfg()" [clientId]="clientId()" [sectionId]="section().id" [preview]="preview()" /> }
      @case ('anchor-nav')         { <sec-anchor-nav [config]="cfg()" [preview]="preview()" /> }
      @case ('slider')             { <sec-slider [config]="cfg()" [preview]="preview()" /> }
      @case ('menu-display')       { <sec-menu-display [config]="cfg()" /> }
      @case ('table-reservation')  { <sec-table-reservation [config]="cfg()" /> }
      @case ('room-grid')          { <sec-room-grid [config]="cfg()" /> }
      @case ('listing-search')     { <sec-listing-search [config]="cfg()" /> }
      @case ('class-schedule')     { <sec-class-schedule [config]="cfg()" /> }
      @case ('membership-plans')   { <sec-membership-plans [config]="cfg()" /> }
      @case ('staff-grid')         { <sec-staff-grid [config]="cfg()" /> }
      @case ('appointment-widget') { <sec-appointment-widget [config]="cfg()" /> }
      @case ('text-block')      { <sec-text-block [config]="cfg()" /> }
      @case ('image-block')     { <sec-image-block [config]="cfg()" /> }
      @case ('button-block')    { <sec-button-block [config]="cfg()" /> }
      @case ('row') {
        <div class="mx-auto max-w-7xl px-2 py-6 lg:px-4">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-12">
            @for (col of rowColumns(); track $index) {
              <div [class]="colSpanClass(col.span)">
                @for (child of (col.sections || []); track $index) {
                  <sec-host [section]="toChild(child, $index)" [preview]="preview()" [clientId]="clientId()" [companyUid]="companyUid()" />
                }
                @if (preview() && (col.sections || []).length === 0) {
                  <div class="flex h-full min-h-24 items-center justify-center rounded-lg border border-dashed border-neutral-300 text-xs text-neutral-400 dark:border-neutral-700">{{ 'siteEditor.emptyColumn' | transloco }}</div>
                }
              </div>
            }
          </div>
        </div>
      }
      @case ('footer')          { <sec-footer [config]="cfg()" /> }
      @case ('custom-html')     { <div class="mx-auto max-w-7xl px-6 py-8" [innerHTML]="html()"></div> }
      @default {
        @if (preview()) {
          <div class="mx-auto my-2 max-w-7xl rounded-xl border border-dashed border-neutral-300 bg-neutral-50 px-6 py-10 text-center text-sm text-neutral-400 dark:border-neutral-700 dark:bg-neutral-900">
            {{ 'siteEditor.sectionPreview' | transloco: { value: section().sectionType } }}
          </div>
        }
      }
    }
    </div>
  `,
})
export class SectionHost {
  section = input.required<SiteSection>();
  /** Önizleme modunda bilinmeyen tipler placeholder gösterir; canlıda gizli. */
  preview = input<boolean>(false);
  /** İletişim formu lead gönderimi için (0 → yalnız görsel). */
  clientId = input<number>(0);
  /** Şirketin genel kimliği — üye portalı bağlantısını kuran bölümlere geçer. */
  companyUid = input<string | null>(null);

  protected cfg = computed(() => parseConfig(this.section().config));

  /** Bölümün giriş efekti — config'te yoksa efekt yok (sessiz varsayılan). */
  protected anim = computed<string>(() => String(this.cfg()['animation'] ?? ''));

  /** Bölümün kendi yazı rengi — boşsa tema/varsayılan geçerli. */
  protected textColor = computed<string>(() => String(this.cfg()['textColor'] ?? '').trim());
  /** Bölümün buton rengi — boşsa tema rengi (--site-primary/accent) kalır. */
  protected buttonColor = computed<string>(() => String(this.cfg()['buttonColor'] ?? '').trim());
  protected buttonTextColor = computed<string>(() => String(this.cfg()['buttonTextColor'] ?? '').trim());

  /** Bölümün yazı ölçeği — boş ya da 1 ise zoom hiç uygulanmaz. */
  protected fontScale = computed<number | null>(() => {
    const v = Number(this.cfg()['fontScale']);
    return Number.isFinite(v) && v > 0 && v !== 1 ? v : null;
  });
  protected html = computed(() => this.cfg()['html'] ?? '');

  // ── Satır / kolon (row) ──
  protected rowColumns = computed<any[]>(() => Array.isArray(this.cfg()['columns']) ? this.cfg()['columns'] : []);
  protected colSpanClass(span: number) { return COL_SPAN[span] ?? 'md:col-span-6'; }
  /** Kolon içindeki çocuk öğeyi sec-host'un beklediği SiteSection şekline çevir. */
  protected toChild(child: any, idx: number): SiteSection {
    return { id: idx + 1, sectionType: child?.sectionType ?? 'custom-html', config: JSON.stringify(child?.config ?? {}), displayOrder: idx, isVisible: true };
  }
}
